import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import FaqSection from "@/components/FaqSection";
import { SERVICES, getService } from "@/data/site";
import { AREAS_FOR_SERVICE } from "@/data/areas";
import { SERVICE_FAQS } from "@/data/faqs";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getService(slug);

  useEffect(() => {
    document.title = service ? `${service.title} — Click World Online` : "Service — Click World Online";
  }, [service]);

  if (!service) {
    return (
      <section className="bg-navy-deep min-h-screen flex items-center">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-40 text-center w-full">
          <h1 className="font-display text-4xl md:text-6xl font-extrabold text-mist tracking-tight">Service not found.</h1>
          <Link to="/services" data-testid="service-not-found-back" className="mt-8 inline-flex items-center gap-2 text-electric font-semibold">
            View all services <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    );
  }

  const related = service.related.map(getService).filter(Boolean);
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      <PageHeader eyebrow={`Service ${String(idx + 1).padStart(2, "0")} / ${String(SERVICES.length).padStart(2, "0")}`} title={service.title} text={service.short} breadcrumb={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: service.title }]} />

      <section className="bg-navy-deep" data-testid="service-overview-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Overview" title={<>What it is.</>} />
            <Reveal delay={0.1}>
              <p className="mt-6 text-slatec text-base md:text-lg leading-relaxed max-w-2xl">{service.overview}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <h3 className="mt-14 font-display text-2xl font-extrabold text-mist tracking-tight">Who it's for</h3>
              <p className="mt-4 text-slatec leading-relaxed max-w-2xl">{service.whoFor}</p>
            </Reveal>
            <Reveal delay={0.24}>
              <h3 className="mt-14 font-display text-2xl font-extrabold text-mist tracking-tight">Our approach</h3>
              <div className="mt-6 flex flex-col gap-4">
                {service.approach.map((step, i) => (
                  <div key={step} className="flex items-center gap-4 bg-navy-light border border-white/10 rounded-2xl px-6 py-4">
                    <span className="font-display text-sm font-bold text-electric">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-mist/85">{step}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-navy-light border border-white/10 rounded-3xl p-8 lg:p-10 grain relative overflow-hidden lg:sticky lg:top-28">
                <div className="absolute inset-0 dot-grid opacity-15" aria-hidden="true" />
                <div className="relative">
                  <h3 className="font-display text-xl font-extrabold text-mist tracking-tight">What's included</h3>
                  <ul className="mt-6 space-y-4">
                    {service.included.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-slatec">
                        <Check size={18} className="text-electric shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid="service-detail-cta"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-electric hover:text-navy-deep text-white font-semibold px-8 py-4 rounded-full transition-colors duration-300"
                  >
                    Discuss this service <ArrowUpRight size={18} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-mist text-navy dot-grid-light" data-testid="service-benefits-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionHeading dark={false} eyebrow="Benefits" title={<>What you get <span className="text-brand">out of it.</span></>} />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {service.benefits.map((b, i) => (
              <Reveal key={b} delay={0.06 * i}>
                <div className="bg-white border border-navy/10 rounded-2xl p-6 h-full hover:border-brand/40 transition-colors duration-300">
                  <span className="font-display text-sm font-bold text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 font-medium text-navy/85">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FaqSection
        title={<>{service.title} <span className="text-brand">FAQs.</span></>}
        items={SERVICE_FAQS[service.slug]}
        idPrefix="service-faq"
      />

      <section className="bg-navy-deep border-t border-white/5" data-testid="related-services-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row justify-between gap-10">
            <div>
              <h2 className="font-display text-2xl lg:text-3xl font-extrabold text-mist tracking-tight">Related services</h2>
              <div className="mt-6 flex flex-wrap gap-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/services/${r.slug}`}
                    data-testid={`related-service-${r.slug}`}
                    className="border border-white/15 text-mist/80 hover:border-electric hover:text-electric rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
                  >
                    {r.title}
                  </Link>
                ))}
              </div>
              {AREAS_FOR_SERVICE[service.slug] && (
                <>
                  <h3 className="mt-8 font-display text-lg font-extrabold text-mist tracking-tight">Available in your city</h3>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {AREAS_FOR_SERVICE[service.slug].map((a) => (
                      <Link
                        key={a.path}
                        to={a.path}
                        data-testid={`service-area-${a.label.toLowerCase()}`}
                        className="border border-electric/30 text-electric rounded-full px-5 py-2.5 text-sm font-medium hover:bg-electric/10 transition-colors duration-300"
                      >
                        {service.title} in {a.label}
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
            <Link
              to={`/services/${next.slug}`}
              data-testid="next-service-link"
              className="group flex items-center gap-4 lg:text-right shrink-0"
            >
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-mist/40">Next service</span>
                <span className="block font-display text-xl lg:text-2xl font-extrabold text-mist group-hover:text-electric transition-colors duration-300">{next.title}</span>
              </span>
              <ArrowRight size={22} className="text-electric group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>

      <CTASection title={`Ready to make ${service.title.toLowerCase()} work for you?`} />
    </>
  );
};

export default ServiceDetail;
